import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './inbox.css';
import { getSupabase } from '../lib/supabase.js';

// Status pill colors. Keys match agent_emails.status values written by
// ExecutorPoll.gs (`new`, `processing`, `processed`, `review`, `error`,
// `ignored`, `unknown`). `unknown` is the "needs triage / no rule yet"
// bucket — surfaced prominently because that's where you teach the agent.
const STATUS_STYLES = {
  new:        { label: 'New',       dot: '#94a3b8' },
  processing: { label: '…',         dot: '#fbbf24' },
  processed:  { label: '✓',         dot: '#10b981' },
  review:     { label: 'Review',    dot: '#f59e0b' },
  error:      { label: 'Error',     dot: '#ef4444' },
  ignored:    { label: 'Ignored',   dot: '#cbd5e1' },
  unknown:    { label: 'Unknown',   dot: '#64748b' },
};

const STATUS_FILTERS = [
  { value: 'all',       label: 'All' },
  { value: 'unknown',   label: 'Unknown' },
  { value: 'review',    label: 'Review' },
  { value: 'processed', label: 'Processed' },
  { value: 'error',     label: 'Errors' },
];

const DATE_FILTERS = [
  { value: 7,   label: '7 days'  },
  { value: 30,  label: '30 days' },
  { value: 90,  label: '90 days' },
  { value: 365, label: 'All'     },
];

const PAGE_SIZE = 100;

export default function Inbox({ profile, navigate }) {
  const sb = getSupabase();
  const isAdmin = profile?.role === 'admin';

  const [rows, setRows]         = useState([]);
  const [senders, setSenders]   = useState([]);
  const [inboxes, setInboxes]   = useState([]);
  const [status, setStatus]     = useState('loading');
  const [error, setError]       = useState(null);
  const [filters, setFilters]   = useState({
    member: 'all', sender: 'all', status: 'all', days: 7, query: '',
  });
  const [selected, setSelected] = useState(null);
  // Refresh-now state: { state: 'idle'|'running'|'done'|'error', message?, runAt? }
  const [refresh, setRefresh]   = useState({ state: 'idle' });
  const [pollNowCfg, setPollNowCfg] = useState(null);  // { url, token }

  const load = useCallback(async () => {
    if (!sb) { setStatus('error'); setError(new Error('Supabase not available')); return; }
    setStatus('loading');
    try {
      const since = new Date(Date.now() - filters.days * 86400000).toISOString();
      let q = sb.from('agent_emails').select('*').gte('received_at', since)
        .order('received_at', { ascending: false, nullsFirst: false })
        .limit(PAGE_SIZE);
      if (filters.status !== 'all')  q = q.eq('status', filters.status);
      if (filters.member !== 'all')  q = q.eq('source_member_email', filters.member);
      if (filters.sender !== 'all')  q = q.eq('sender_id', filters.sender);
      const [emailsRes, sendersRes, inboxesRes] = await Promise.all([
        q,
        sb.from('inv_email_senders').select('id, name, bg_color, text_color'),
        sb.from('connected_inboxes').select('gmail_address:email, display_name, bg_color, text_color'),
      ]);
      if (emailsRes.error)  throw emailsRes.error;
      setRows(emailsRes.data || []);
      setSenders(sendersRes.data || []);
      setInboxes(inboxesRes.data || []);
      setStatus('ready');
    } catch (err) {
      setStatus('error'); setError(err);
    }
  }, [sb, filters.days, filters.status, filters.member, filters.sender]);

  useEffect(() => { load(); }, [load]);

  // Fetch poll-now config (URL + token) once. Stored in app_settings.
  useEffect(() => {
    if (!sb) return;
    let cancelled = false;
    (async () => {
      const { data } = await sb.from('app_settings').select('value').eq('key', 'agent_poll_now').maybeSingle();
      if (!cancelled && data?.value?.url && data?.value?.token) {
        setPollNowCfg(data.value);
      }
    })();
    return () => { cancelled = true; };
  }, [sb]);

  // Triggers a one-shot poll on the agent (no waiting for the cron),
  // then reloads the email list. Apps Script's doGet returns JSON.
  const refreshNow = useCallback(async () => {
    if (!pollNowCfg || refresh.state === 'running') return;
    setRefresh({ state: 'running' });
    try {
      const u = new URL(pollNowCfg.url);
      u.searchParams.set('action', 'poll-now');
      u.searchParams.set('token', pollNowCfg.token);
      // Apps Script's web app responds with 302 to googleusercontent.com,
      // which the browser follows. `redirect: 'follow'` is the default.
      const resp = await fetch(u.toString(), { method: 'GET' });
      const json = await resp.json();
      if (!json.ok) throw new Error(json.error || 'agent returned not-ok');
      setRefresh({
        state: 'done', runAt: json.ran_at, processed: json.processed,
        skipped: json.skipped, inboxes: Array.isArray(json.inboxes) ? json.inboxes : [],
      });
      // Give the agent a beat to finish inserts, then reload.
      setTimeout(() => load(), 1500);
    } catch (err) {
      setRefresh({ state: 'error', message: String(err?.message || err) });
    }
  }, [pollNowCfg, refresh.state, load]);

  // Free-text query is applied client-side so it can match across multiple
  // columns (from, subject, snippet) without round-tripping per keystroke.
  const visible = useMemo(() => {
    const q = filters.query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.from_email   || '').toLowerCase().includes(q) ||
      (r.from_name    || '').toLowerCase().includes(q) ||
      (r.subject      || '').toLowerCase().includes(q) ||
      (r.snippet      || '').toLowerCase().includes(q) ||
      (r.sender_name  || '').toLowerCase().includes(q)
    );
  }, [rows, filters.query]);

  const senderById  = useMemo(() => Object.fromEntries(senders.map((s) => [s.id, s])), [senders]);
  const inboxByMail = useMemo(() => Object.fromEntries(inboxes.map((i) => [i.gmail_address.toLowerCase(), i])), [inboxes]);

  // Status counts shown next to the filter chips
  const statusCounts = useMemo(() => {
    const counts = { all: rows.length };
    rows.forEach((r) => { counts[r.status] = (counts[r.status] || 0) + 1; });
    return counts;
  }, [rows]);

  return (
    <div className="inbox-screen">
      <header className="ib-header">
        <div>
          <div className="ib-eyebrow">FinOps · Email activity</div>
          <h1 className="ib-title">Inbox</h1>
          <p className="ib-sub">
            Every email the agent has seen across connected member inboxes.
            Filter by status to find what needs your attention.
          </p>
        </div>
        <div className="ib-header-actions">
          {isAdmin && pollNowCfg && (
            <button
              className="ib-refresh"
              onClick={refreshNow}
              disabled={refresh.state === 'running'}
              title="Trigger an immediate poll on every connected member's inbox"
            >
              {refresh.state === 'running' ? '⟳ Polling…' : '⟳ Refresh now'}
            </button>
          )}
          {navigate && (
            <button className="ib-back" onClick={() => navigate('home')}>← Back</button>
          )}
        </div>
      </header>

      {/* Filter bar */}
      <div className="ib-filters">
        <input
          className="ib-search"
          placeholder="Search subject, sender, body…"
          value={filters.query}
          onChange={(e) => setFilters({ ...filters, query: e.target.value })}
        />
        <select
          className="ib-select"
          value={filters.member}
          onChange={(e) => setFilters({ ...filters, member: e.target.value })}
        >
          <option value="all">All members</option>
          {inboxes.map((i) => (
            <option key={i.gmail_address} value={i.gmail_address}>{i.display_name || i.gmail_address}</option>
          ))}
        </select>
        <select
          className="ib-select"
          value={filters.sender}
          onChange={(e) => setFilters({ ...filters, sender: e.target.value })}
        >
          <option value="all">All senders</option>
          {senders.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
        <select
          className="ib-select"
          value={filters.days}
          onChange={(e) => setFilters({ ...filters, days: Number(e.target.value) })}
        >
          {DATE_FILTERS.map((d) => <option key={d.value} value={d.value}>Last {d.label}</option>)}
        </select>
      </div>

      {/* Refresh-now status banner — per-inbox results so a broken connection
          is visible immediately instead of looking like "nothing happened". */}
      {refresh.state === 'done' && refresh.skipped && (
        <p className="ib-refresh-note">⏱ Agent already polled within the last 20s — try again shortly.</p>
      )}
      {refresh.state === 'done' && !refresh.skipped && (() => {
        const inb = refresh.inboxes || [];
        const FAIL = ['auth_failed', 'list_failed', 'revoked', 'error'];
        const ok = inb.filter((i) => i.status === 'read' || i.status === 'empty');
        const failed = inb.filter((i) => FAIL.includes(i.status));
        const labelFor = (email) => inboxByMail[(email || '').toLowerCase()]?.display_name || email;
        return (
          <div className="ib-refresh-note">
            ✓ Polled {inb.length} inbox{inb.length === 1 ? '' : 'es'} · {ok.length} reading
            {' · '}{refresh.processed || 0} new email{refresh.processed === 1 ? '' : 's'}.
            {failed.length > 0 && (
              <div className="ib-refresh-fail">
                ✗ {failed.length} couldn’t be read — needs reconnect (the person must approve “Read your email” on Google’s screen):
                <ul>
                  {failed.map((f) => (
                    <li key={f.email}>
                      <strong>{labelFor(f.email)}</strong>
                      <span className="ib-muted"> — {f.reason || f.status}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })()}
      {refresh.state === 'error' && (
        <p className="ib-error">Refresh failed: {refresh.message}</p>
      )}

      {/* Status chips */}
      <div className="ib-chips">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s.value}
            className={'ib-chip' + (filters.status === s.value ? ' ib-chip-on' : '')}
            onClick={() => setFilters({ ...filters, status: s.value })}
          >
            {s.label}
            <span className="ib-chip-count">{statusCounts[s.value === 'all' ? 'all' : s.value] || 0}</span>
          </button>
        ))}
      </div>

      {/* Body */}
      {status === 'loading' && <p className="ib-muted">Loading…</p>}
      {status === 'error'   && <p className="ib-error">Couldn't load emails: {String(error?.message || error)}</p>}

      {status === 'ready' && (
        <div className={'ib-grid' + (selected ? ' ib-grid-with-panel' : '')}>
          <div className="ib-list">
            {visible.length === 0 && (
              <div className="ib-empty">
                {rows.length === 0
                  ? 'No emails yet. Once a member connects their Gmail via /connect-gmail, every matching email shows up here within 15 min.'
                  : 'No emails match the current filters.'}
              </div>
            )}
            {visible.map((r) => (
              <EmailRow
                key={r.id}
                row={r}
                sender={senderById[r.sender_id]}
                inbox={inboxByMail[(r.source_member_email || '').toLowerCase()]}
                selected={selected?.id === r.id}
                onClick={() => setSelected(r)}
              />
            ))}
          </div>

          {selected && (
            <DetailPanel
              row={selected}
              sender={senderById[selected.sender_id]}
              inbox={inboxByMail[(selected.source_member_email || '').toLowerCase()]}
              isAdmin={isAdmin}
              pollNowCfg={pollNowCfg}
              onClose={() => setSelected(null)}
              onChange={() => load()}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── EmailRow ─────────────────────────────────────────────────────────────
function EmailRow({ row, sender, inbox, selected, onClick }) {
  const memberColor = inbox?.bg_color || '#cbd5e1';
  const senderBg    = sender?.bg_color   || '#e2e8f0';
  const senderText  = sender?.text_color || '#475569';
  const st = STATUS_STYLES[row.status] || STATUS_STYLES.new;
  const ts = row.received_at ? new Date(row.received_at) : null;
  const tsLabel = ts ? _relTime(ts) : '';
  const fromLabel = row.from_name || row.from_email || '—';

  return (
    <button className={'ib-row' + (selected ? ' ib-row-selected' : '')} onClick={onClick}>
      <span className="ib-dot" style={{ background: memberColor }} title={inbox?.display_name || row.source_member_email} />
      <span className="ib-sender" style={{ background: senderBg, color: senderText }}>
        {row.sender_name || sender?.name || 'Unknown'}
      </span>
      <span className="ib-from">{fromLabel}</span>
      <span className="ib-subject">{row.subject || <em className="ib-muted">(no subject)</em>}</span>
      <span className="ib-ts">{tsLabel}</span>
      <span className="ib-status" title={row.status}>
        <span className="ib-status-dot" style={{ background: st.dot }} />
        {st.label}
      </span>
    </button>
  );
}

// ─── DetailPanel ──────────────────────────────────────────────────────────
function DetailPanel({ row, sender, inbox, isAdmin, pollNowCfg, onClose, onChange }) {
  const sb = getSupabase();
  const memberName = inbox?.display_name || row.source_member_email;
  const senderName = sender?.name || row.sender_name || 'Unknown sender';

  const setStatus = async (next) => {
    if (!isAdmin) return;
    const { error: err } = await sb.from('agent_emails').update({ status: next }).eq('id', row.id);
    if (!err) onChange?.();
  };

  return (
    <aside className="ib-panel">
      <header className="ib-panel-head">
        <button className="ib-panel-close" onClick={onClose}>✕</button>
        <div className="ib-panel-eyebrow">
          <span className="ib-sender" style={{ background: sender?.bg_color || '#e2e8f0', color: sender?.text_color || '#475569' }}>
            {senderName}
          </span>
          <span className="ib-muted ib-panel-from"> · {memberName}</span>
        </div>
        <h2 className="ib-panel-title">{row.subject || '(no subject)'}</h2>
        <p className="ib-panel-meta">
          From <strong>{row.from_email}</strong>
          {row.received_at && <> · {new Date(row.received_at).toLocaleString()}</>}
        </p>
      </header>

      <section className="ib-panel-body">
        <div className="ib-panel-snippet">{row.snippet || <em className="ib-muted">No preview available.</em>}</div>
        <p className="ib-panel-note">
          Full body lives in <code>unichurn1@gmail.com</code> for archive.
          On-demand body fetch is wired up next.
        </p>
      </section>

      {row.extracted_fields && (
        <section className="ib-panel-section">
          <h3 className="ib-panel-h3">Extracted</h3>
          <pre className="ib-panel-json">{JSON.stringify(row.extracted_fields, null, 2)}</pre>
          {row.linked_record_id && (
            <p className="ib-panel-meta">
              Linked to {row.linked_record_type}: <code>{row.linked_record_id}</code>
            </p>
          )}
        </section>
      )}

      {row.email_type && (
        <section className="ib-panel-section">
          <h3 className="ib-panel-h3">Classification</h3>
          <p><code>{row.email_type}</code></p>
          {row.llm_model && <p className="ib-muted">{row.llm_model}{row.llm_cost_usd != null && <> · ~${Number(row.llm_cost_usd).toFixed(4)}</>}</p>}
        </section>
      )}

      {row.notes && (
        <section className="ib-panel-section">
          <h3 className="ib-panel-h3">Notes</h3>
          <p className="ib-muted">{row.notes}</p>
        </section>
      )}

      {isAdmin && (
        <section className="ib-panel-actions">
          {row.status !== 'ignored' && (
            <button className="ib-btn-ghost" onClick={() => setStatus('ignored')}>Mark ignored</button>
          )}
          {row.status === 'ignored' && (
            <button className="ib-btn-ghost" onClick={() => setStatus('new')}>Un-ignore</button>
          )}
          {row.status === 'review' && (
            <button className="ib-btn-ghost" onClick={() => setStatus('processed')}>Mark processed</button>
          )}
        </section>
      )}

      {/* "Teach the agent" — only shown for unknown emails with an identified sender */}
      {isAdmin && row.sender_id && (row.status === 'unknown' || row.status === 'new') && (
        <TeachPanel row={row} sender={sender} pollNowCfg={pollNowCfg} onChange={onChange} />
      )}
    </aside>
  );
}

// ─── TeachPanel ───────────────────────────────────────────────────────────
// Lets the admin define a sender_handlers rule for this email's sender and
// apply it to verify the LLM extracts what they expect.
function TeachPanel({ row, sender, pollNowCfg, onChange }) {
  const sb = getSupabase();
  const [existing, setExisting]   = useState([]);
  const [mode, setMode]           = useState('list');   // 'list' | 'edit'
  const [draft, setDraft]         = useState({ id: null, name: '', subject_pattern: '', extraction_fields: [{ name: '', description: '' }] });
  const [saving, setSaving]       = useState(false);
  const [applying, setApplying]   = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [error, setError]         = useState(null);

  const reloadRules = useCallback(async () => {
    if (!sb || !row.sender_id) return;
    const { data, error: err } = await sb.from('sender_handlers')
      .select('*').eq('sender_id', row.sender_id)
      .order('created_at', { ascending: true });
    if (!err) setExisting(data || []);
  }, [sb, row.sender_id]);

  useEffect(() => { reloadRules(); }, [reloadRules]);

  const startNew = () => {
    setDraft({
      id: null,
      name: '',
      subject_pattern: (row.subject || '').split(/[:\-—#]/)[0].trim().substring(0, 40),
      extraction_fields: [{ name: '', description: '' }],
    });
    setApplyResult(null);
    setMode('edit');
  };

  const startEdit = (h) => {
    setDraft({
      id: h.id,
      name: h.name || '',
      subject_pattern: h.subject_pattern || '',
      extraction_fields: (h.extraction_fields && h.extraction_fields.length)
        ? h.extraction_fields
        : [{ name: '', description: '' }],
    });
    setApplyResult(null);
    setMode('edit');
  };

  const addField    = () => setDraft({ ...draft, extraction_fields: [...draft.extraction_fields, { name: '', description: '' }] });
  const updateField = (i, k, v) => {
    const next = draft.extraction_fields.slice();
    next[i] = { ...next[i], [k]: v };
    setDraft({ ...draft, extraction_fields: next });
  };
  const removeField = (i) => {
    const next = draft.extraction_fields.filter((_, idx) => idx !== i);
    setDraft({ ...draft, extraction_fields: next.length ? next : [{ name: '', description: '' }] });
  };

  const saveRule = async () => {
    if (!draft.name.trim()) { setError(new Error('Rule name is required')); return; }
    const cleanFields = draft.extraction_fields
      .map((f) => ({ name: (f.name || '').trim(), description: (f.description || '').trim() }))
      .filter((f) => f.name);
    if (!cleanFields.length) { setError(new Error('Add at least one field to extract')); return; }
    setSaving(true); setError(null);
    const payload = {
      sender_id: row.sender_id,
      name: draft.name.trim(),
      subject_pattern: draft.subject_pattern.trim() || null,
      extraction_fields: cleanFields,
    };
    let res;
    if (draft.id) {
      res = await sb.from('sender_handlers').update(payload).eq('id', draft.id).select().single();
    } else {
      res = await sb.from('sender_handlers').insert(payload).select().single();
    }
    setSaving(false);
    if (res.error) { setError(res.error); return; }
    setDraft({ ...draft, id: res.data.id });
    await reloadRules();
  };

  const applyToEmail = async (handlerId) => {
    if (!pollNowCfg) { setError(new Error('Agent endpoint not configured (app_settings.agent_poll_now missing)')); return; }
    setApplying(true); setApplyResult(null); setError(null);
    try {
      const u = new URL(pollNowCfg.url);
      u.searchParams.set('action', 'apply-handler');
      u.searchParams.set('token', pollNowCfg.token);
      u.searchParams.set('handler_id', handlerId);
      u.searchParams.set('email_id', row.id);
      const resp = await fetch(u.toString());
      const json = await resp.json();
      setApplying(false);
      if (!json.ok) { setError(new Error(json.error || json.reason || 'agent failed')); return; }
      setApplyResult(json);
      // Give the agent a moment, then reload the email row so the new
      // extracted_fields show up in the panel.
      setTimeout(() => onChange?.(), 1200);
    } catch (err) {
      setApplying(false); setError(err);
    }
  };

  const deleteRule = async (handlerId) => {
    if (!window.confirm('Delete this rule? Future emails from this sender that matched it will go back to Unknown.')) return;
    const { error: err } = await sb.from('sender_handlers').delete().eq('id', handlerId);
    if (!err) {
      await reloadRules();
      if (draft.id === handlerId) { setMode('list'); setDraft({ id: null, name: '', subject_pattern: '', extraction_fields: [{ name: '', description: '' }] }); }
    }
  };

  return (
    <section className="ib-teach">
      <h3 className="ib-panel-h3">Teach the agent</h3>
      <p className="ib-teach-help">
        Define a rule so future emails from <strong>{sender?.name || 'this sender'}</strong> auto-extract the right fields.
      </p>

      {mode === 'list' && (
        <>
          {existing.length === 0 && (
            <p className="ib-muted ib-teach-empty">No rules yet for this sender.</p>
          )}
          {existing.map((h) => (
            <div key={h.id} className="ib-teach-rule">
              <div className="ib-teach-rule-head">
                <strong>{h.name}</strong>
                {h.subject_pattern && <span className="ib-teach-pattern">when subject contains "{h.subject_pattern}"</span>}
              </div>
              <div className="ib-teach-rule-fields">
                {(h.extraction_fields || []).map((f) => (
                  <code key={f.name} className="ib-teach-field">{f.name}</code>
                ))}
              </div>
              <div className="ib-teach-rule-actions">
                <button className="ib-btn-ghost" onClick={() => applyToEmail(h.id)} disabled={applying}>
                  {applying ? 'Applying…' : 'Apply to this email'}
                </button>
                <button className="ib-btn-ghost" onClick={() => startEdit(h)}>Edit</button>
                <button className="ib-btn-ghost ib-btn-danger" onClick={() => deleteRule(h.id)}>Delete</button>
              </div>
            </div>
          ))}
          <button className="ib-teach-add" onClick={startNew}>+ New rule</button>
        </>
      )}

      {mode === 'edit' && (
        <div className="ib-teach-form">
          <label className="ib-teach-field-row">
            <span>Rule name</span>
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Coinbase deposit" />
          </label>
          <label className="ib-teach-field-row">
            <span>Subject pattern (optional)</span>
            <input value={draft.subject_pattern} onChange={(e) => setDraft({ ...draft, subject_pattern: e.target.value })} placeholder='e.g. "You received"' />
            <small>Case-insensitive substring. Leave blank to match every email from this sender.</small>
          </label>

          <div className="ib-teach-fields-label">Fields to extract</div>
          {draft.extraction_fields.map((f, i) => (
            <div key={i} className="ib-teach-field-grid">
              <input
                value={f.name}
                onChange={(e) => updateField(i, 'name', e.target.value)}
                placeholder="field_name"
                className="ib-teach-field-name"
              />
              <input
                value={f.description}
                onChange={(e) => updateField(i, 'description', e.target.value)}
                placeholder="What this field is — e.g. 'deposit amount in USD'"
              />
              <button className="ib-teach-field-x" onClick={() => removeField(i)} title="Remove field">✕</button>
            </div>
          ))}
          <button className="ib-teach-add-field" onClick={addField}>+ Add field</button>

          <div className="ib-teach-form-actions">
            <button className="ib-btn-ghost" onClick={() => setMode('list')}>Back</button>
            <button className="ib-btn-primary" onClick={saveRule} disabled={saving}>
              {saving ? 'Saving…' : (draft.id ? 'Update rule' : 'Save rule')}
            </button>
            {draft.id && (
              <button className="ib-btn-primary" onClick={() => applyToEmail(draft.id)} disabled={applying}>
                {applying ? 'Applying…' : 'Apply to this email'}
              </button>
            )}
          </div>
        </div>
      )}

      {error && <p className="ib-error" style={{ marginTop: 10 }}>{String(error.message || error)}</p>}
      {applyResult && (
        <div className="ib-teach-result">
          <strong>Extracted</strong> · {applyResult.model} · ~${Number(applyResult.cost_usd || 0).toFixed(4)}
          <pre className="ib-panel-json" style={{ marginTop: 8 }}>{JSON.stringify(applyResult.data, null, 2)}</pre>
        </div>
      )}
    </section>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────
function _relTime(d) {
  const ms = Date.now() - d.getTime();
  const m  = Math.round(ms / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return m + 'm';
  const h = Math.round(m / 60);
  if (h < 24) return h + 'h';
  const days = Math.round(h / 24);
  if (days < 7) return days + 'd';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
